import React, { useState } from 'react';
import { Box } from '@chakra-ui/react';
import { getDockSlideUpCss, TRANSITION_COMPOSER_DOCK_AXIS } from '@/components/ui/motion.ts';
import { ASSISTANT_CONTENT_WIDTH } from './composerConstants.ts';
import { useComposerDockAxis } from './composerDockAxis.ts';
import { ZIndex } from '@/utils/zIndex.ts';
import { ComposerDockFocusContext } from './composerDockFocus.ts';
import ComposerFocusBackdrop from './ComposerFocusBackdrop.tsx';

// Bottom backdrop heights: the solid floor occludes the screen edge; the fade is the taller, softer
// variant for pages that want the content to dissolve under the bar rather than meet a hard line.
const BOTTOM_FADE_HEIGHT = { mobile: '104px', desktop: '96px' } as const;
const SOLID_FLOOR_HEIGHT = { mobile: '88px', desktop: '64px' } as const;
// Fades the page out under the floating bar. Matches surface.canvas (the solid floor's bg) so the
// two backdrops read as one surface; color-mix supplies the transparent stop the gradient needs.
const canvasAlpha = (percent: number) =>
	`color-mix(in srgb, var(--chakra-colors-surface-canvas) ${percent}%, transparent)`;
const BOTTOM_FADE_GRADIENT = `linear-gradient(to top, ${canvasAlpha(78)}, ${canvasAlpha(0)})`;
const MOBILE_SOLID_FLOOR_GRADIENT = `linear-gradient(to top, ${canvasAlpha(100)} 0%, ${canvasAlpha(100)} 66%, ${canvasAlpha(0)} 100%)`;

interface ComposerDockProps {
	children: React.ReactNode;
	/** Anchor the dock to its positioned parent instead of estimating that parent's viewport bounds. */
	contained?: boolean;
	/**
	 * Slide up from the bottom edge on mount (the assistant's chat-entry transition). Off by
	 * default — the records bar is always present, so it just sits there.
	 */
	slideUpOnMount?: boolean;
	/** Render the solid bottom floor behind the floating composer. */
	showSolidFloor?: boolean;
	/** Render a subtle transparent fade behind the floating composer. */
	showBottomFade?: boolean;
	/**
	 * Content floated just above the bar, anchored to its top edge — so it tracks the bar's height
	 * (collapsed or grown into add mode) instead of a fixed viewport offset. Used for the
	 * jump-to-latest button.
	 */
	aboveBar?: React.ReactNode;
}

/**
 * The single bottom shell for the composer/search bar, shared by the assistant and the records
 * page. By default it mirrors the inset content card the page scrolls inside (see
 * _authenticated.tsx) so the bar centres on the same axis as the content: the card sits at the
 * sidebar's right edge behind a 1px border, with an 8px right margin and a 6px reserved
 * scrollbar gutter. This shell is viewport-anchored, so it subtracts those itself (plus a 2px
 * fine nudge, both edges, for sub-pixel rounding) instead of going edge-to-edge.
 *
 * In a split workspace it is instead contained by the main panel. That makes the panel edge the
 * composer's real boundary; no independently timed width measurement can let it paint underneath
 * the adjacent detail panel while the divider moves.
 */
const ComposerDock: React.FC<ComposerDockProps> = (props) => {
	const {
		children,
		contained = false,
		slideUpOnMount = false,
		showSolidFloor = true,
		showBottomFade = false,
		aboveBar,
	} = props;
	const [isComposerFocused, setIsComposerFocused] = useState(false);
	const { left, right, isMobile, sidebarWidth } = useComposerDockAxis();
	const position = contained ? 'absolute' : 'fixed';
	const dockLeft = contained ? '0px' : left;
	const dockRight = contained ? '0px' : right;
	const backdropLeft = contained ? '0px' : sidebarWidth;

	const shellCss = slideUpOnMount ? getDockSlideUpCss(isMobile) : undefined;
	const dockCss = {
		...shellCss,
		transition: shellCss?.transition
			? `${shellCss.transition}, ${TRANSITION_COMPOSER_DOCK_AXIS}`
			: TRANSITION_COMPOSER_DOCK_AXIS,
	};

	return (
		<ComposerDockFocusContext.Provider value={setIsComposerFocused}>
			{isComposerFocused ? <ComposerFocusBackdrop /> : null}
			<Box
				className="composer-dock-shell"
				css={dockCss}
				style={{
					position,
					bottom: 0,
					left: dockLeft,
					right: dockRight,
					zIndex: isComposerFocused ? ZIndex.Composer : 10,
					background: 'transparent',
				}}
				borderRadius={0}
			>
				{/* No background: the bar and its + are opaque on their own, so they float over the
				    content. A full-width bg block would occlude a band of the content as the bar grows. */}
				<Box px={{ base: 4, md: 0 }} pb={{ base: 3, md: 4 }}>
					<Box w={ASSISTANT_CONTENT_WIDTH} mx="auto" position="relative">
						{/* Anchored to the bar's top edge, so it floats just above the bar at any height —
						    collapsed or grown into add mode — with no fixed offset to drift out of place. */}
						{aboveBar && (
							<Box
								position="absolute"
								bottom="calc(100% + 40px)"
								left={0}
								right={0}
								display="flex"
								justifyContent="center"
								pointerEvents="none"
							>
								{aboveBar}
							</Box>
						)}
						{children}
					</Box>
				</Box>
			</Box>

			{showBottomFade && (
				<Box
					aria-hidden
					pointerEvents="none"
					style={{
						position,
						bottom: 0,
						left: backdropLeft,
						right: dockRight,
						height: isMobile ? BOTTOM_FADE_HEIGHT.mobile : BOTTOM_FADE_HEIGHT.desktop,
						zIndex: 9,
						background: BOTTOM_FADE_GRADIENT,
						transition: TRANSITION_COMPOSER_DOCK_AXIS,
					}}
				/>
			)}

			{showSolidFloor && (
				/* Solid floor beneath the floating bar so content doesn't peek through at the screen
				   edge. Fixed height (doesn't grow with the bar), sits behind it via a lower zIndex. */
				<Box
					aria-hidden
					pointerEvents="none"
					bg="surface.canvas"
					style={{
						position,
						bottom: 0,
						left: backdropLeft,
						right: dockRight,
						height: isMobile ? SOLID_FLOOR_HEIGHT.mobile : SOLID_FLOOR_HEIGHT.desktop,
						zIndex: 9,
						background: isMobile ? MOBILE_SOLID_FLOOR_GRADIENT : undefined,
						transition: TRANSITION_COMPOSER_DOCK_AXIS,
					}}
				/>
			)}
		</ComposerDockFocusContext.Provider>
	);
};

export default ComposerDock;
